<template>
  <form-block
    v-if="facebookAttributionWindows.length !== 0"
    :title="tr('Attribution window')"
  >
    <template v-if="isIosAppPromo">
      <ui-radio-group model-value="default" disabled>
        <label class="flex w-fit items-center gap-2 text-sm text-foreground">
          <ui-radio-group-item value="default" />
          {{ tr('Default') }}
        </label>
      </ui-radio-group>
      <info-text>
        {{ tr('Attribution window cannot be customized for iOS app promotion campaigns.') }}
      </info-text>
    </template>
    <template v-else>
      <ui-radio-group
        :model-value="isCustom ? 'custom' : 'default'"
        :disabled="disabled"
        class="flex flex-col gap-2.5"
        @update:model-value="(value) => isCustom = value === 'custom'"
      >
        <label class="flex w-fit cursor-pointer items-center gap-2 text-sm text-foreground">
          <ui-radio-group-item value="default" />
          {{ tr('Default') }}
        </label>
        <label class="flex w-fit cursor-pointer items-center gap-2 text-sm text-foreground">
          <ui-radio-group-item value="custom" />
          {{ tr('Custom') }}
        </label>
      </ui-radio-group>

      <div v-if="isCustom" class="flex flex-col gap-3 text-sm text-muted-foreground">
        <p class="m-0">
          {{ tr('The number of days between when a person viewed or clicked your ad and subsequently took an action.') }}
          <a href="https://www.facebook.com/business/help/2198119873776795?id=768381033531365" target="_blank">{{ tr('Learn more') }}</a>
        </p>
        <select-field
          v-model="entity.settings.adset_attribution_window"
          :options="availableAttributionWindows"
          :reduce="(obj) => obj.machine_name"
          :aria-label="tr('Attribution window')"
          :disabled="disabled"
        />
        <p class="m-0">{{ tr('By default Ads Manager uses ad set attribution window, also known as unified attribution window. You may see discrepancies in conversion data if this setting is disabled.') }}</p>
      </div>
    </template>
  </form-block>
</template>

<script>
import RuleFormBlockMixin from '@/mixins/RuleForm/Block.js';
import InfoText from '@/ui/components/InfoText.vue';
import SelectField from '@/ui/components/SelectField.vue';

export default {
  name: 'AttributionWindowOdaxBlock',

  components: {
    InfoText,
    SelectField,
  },

  mixins: [ RuleFormBlockMixin ],

  data: () => ({
    isCustom: null,
  }),

  computed: {
    isIosAppPromo() {
      return this.entity.isIosAppPromo;
    },

    campaignObjective() {
      return this.entity.output_campaign?.objective;
    },

    optimizationGoal() {
      return this.entity.settings?.adset_optimization_goal;
    },

    facebookAttributionWindows() {
      return this.$store.getters['app/facebookAttributionWindows'];
    },

    availableAttributionWindows() {
      const objectives = this.$store.getters['app/facebookOdaxMap'];

      if (!this.campaignObjective || !this.optimizationGoal) {
        return [];
      }

      const objective = objectives.find(obj => obj.machine_name === this.campaignObjective);
      if (!objective) {
        return [];
      }

      let allowedWindows = [];
      for (const destType of objective.destination_types) {
        const goal = destType.optimization_goals.find(g => g.machine_name === this.optimizationGoal);
        if (goal) {
          allowedWindows = goal.allowed_attribution_windows;
          break;
        }
      }

      return this.facebookAttributionWindows.filter(window =>
        allowedWindows.includes(window.machine_name));
    },
  },

  watch: {
    isIosAppPromo(val) {
      if (val) {
        this.isCustom = false;
        this.entity.settings.adset_attribution_window = null;
      }
    },
    isCustom(val, oldVal) {
      if (oldVal === null || val === oldVal) {
        return;
      }

      if (val) {
        this.getAvailableWindows();
      }
      else {
        this.entity.settings.adset_attribution_window = null;
      }
    },
    optimizationGoal() {
      this.getAvailableWindows();
    },
  },

  created() {
    this.isCustom = !!this.entity.settings.adset_attribution_window;
  },

  methods: {
    validate() {
      if (!this.entity.settings.adset_attribution_window) {
        return true;
      }

      return this.availableAttributionWindows.some(window => window.machine_name === this.entity.settings.adset_attribution_window);
    },
    getAvailableWindows() {
      const availableWindows = this.availableAttributionWindows;
      if (availableWindows.length > 0) {
        this.entity.settings.adset_attribution_window = availableWindows[0].machine_name;
      }
    },
  },
};
</script>
